/**
 * One square of ground. `end`, `enter`, and `life` are what every tile has; the rest are the
 * powers a given terrain adds. `tHelp` and `tHarm` in ./forge.ts decide which way each one cuts.
 */
export interface TerrainDef {
	/** Display name. */
	n: string;
	/** Tile colour. */
	c: string;
	/** Damage for ending a turn here. */
	end: number;
	/** Energy charged for stepping in. */
	enter: number;
	/** Rounds the tile survives before it decays. */
	life: number;
	/** One-line description shown on the tile inspector. */
	d: string;
	anchor?: number;
	aura?: number;
	auraHeal?: number;
	bite?: number;
	brittle?: number;
	dead?: number;
	flow?: number;
	gain?: number;
	gone?: number;
	heal?: number;
	hide?: number;
	los?: number;
	/** Terrain key this one turns into when a hot tile lands beside it. */
	melts?: string;
	pull?: number;
	rad?: number;
	reveal?: number;
	root?: number;
	sap?: number;
	scatter?: number;
	shove?: number;
	solid?: number;
	spread?: number;
	ward?: number;
	warp?: number;
}

/** The 105 grounds an element card or a fusion can lay. */
export const T: Record<string, TerrainDef> = {
	lava: {n: "Fire", c: "#ff5a1e", end: 10, enter: 1, life: 999, d: "End your turn here and take 10"},
	ocean: {
		n: "Water",
		c: "#2f9dff",
		end: 0,
		enter: 1,
		life: 999,
		flow: 1,
		d: "Carries you one more tile the way you came",
	},
	stone: {
		n: "Earth",
		c: "#9c7a4d",
		end: 0,
		enter: 2,
		life: 999,
		los: 1,
		d: "Slow to climb, and you are blind while on it",
	},
	gale: {
		n: "Air",
		c: "#9ef0dc",
		end: 0,
		enter: 0,
		life: 5,
		gain: 1,
		d: "Free to cross. Ends your turn with +1 energy",
	},
	charge: {n: "Lightning", c: "#ffd24a", end: 5, enter: 1, life: 4, d: "End your turn here and take 5"},
	glass: {
		n: "Glass",
		c: "#b6f0e6",
		end: 0,
		enter: 1,
		life: 999,
		bite: 9,
		brittle: 1,
		d: "Razor edges. Crossing it cuts for 9 and shatters it for good",
	},
	whirl: {
		n: "Whirlpool",
		c: "#7ce0ff",
		end: 0,
		enter: 1,
		life: 999,
		warp: 1,
		d: "Enter one, come out its twin",
	},
	steam: {
		n: "Steam",
		c: "#dbe9f2",
		end: 0,
		enter: 1,
		life: 3,
		los: 1,
		d: "Blinds you while you stand in it. Burns off fast",
	},
	wildfire: {
		n: "Wildfire",
		c: "#ff3a12",
		end: 12,
		enter: 1,
		life: 3,
		spread: 2,
		d: "Burns a 5x5. End your turn in it and take 12",
	},
	plasma: {n: "Plasma", c: "#ff62e0", end: 8, enter: 1, life: 3, d: "End your turn here and take 8"},
	mud: {
		n: "Mud",
		c: "#7d6136",
		end: 0,
		enter: 2,
		life: 999,
		root: 1,
		d: "Step in and you cannot move until the end of your next turn",
	},
	surge: {
		n: "Surge",
		c: "#5ef0ff",
		end: 12,
		enter: 2,
		life: 3,
		d: "Live water. End your turn here and take 12",
	},
	dust: {
		n: "Dust",
		c: "#c9ae82",
		end: 0,
		enter: 1,
		life: 4,
		los: 1,
		d: "Easy to cross, but you are blind while in it",
	},
	magnet: {
		n: "Magnetite",
		c: "#b06fd8",
		end: 0,
		enter: 2,
		life: 999,
		pull: 1,
		d: "Drags in anyone who ends a turn beside it",
	},
	storm: {
		n: "Storm",
		c: "#b18cff",
		end: 6,
		enter: 1,
		life: 3,
		shove: 1,
		d: "Take 6 and get thrown a tile",
	},
	space: {
		n: "Space",
		c: "#c9a6ff",
		end: 0,
		enter: 1,
		life: 999,
		gone: 1,
		d: "Set foot here and you are out of the match",
	},
	nova: {n: "Nova", c: "#ff8fd0", end: 25, enter: 1, life: 2, d: "End your turn here and take 25"},
	rift: {
		n: "Rift",
		c: "#00e0c8",
		end: 0,
		enter: 1,
		life: 999,
		scatter: 1,
		d: "Flings you to a random open tile",
	},
	meteor: {
		n: "Meteor",
		c: "#b05a2a",
		end: 20,
		enter: 3,
		life: 999,
		d: "End your turn here and take 20. Permanent",
	},
	vacuum: {
		n: "Vacuum",
		c: "#ded4ff",
		end: 0,
		enter: 1,
		life: 4,
		pull: 2,
		d: "Drags in anyone ending a turn within two tiles",
	},
	pulsar: {
		n: "Pulsar",
		c: "#eaff5c",
		end: 15,
		enter: 1,
		life: 3,
		shove: 1,
		d: "Take 15 and get thrown a tile",
	},
	venom: {
		n: "Poison",
		c: "#7ed321",
		end: 8,
		enter: 1,
		life: 999,
		bite: 6,
		d: "6 to cross it, 8 more to end your turn in it. Permanent",
	},
	blight: {
		n: "Blight",
		c: "#c9d900",
		end: 18,
		enter: 1,
		life: 3,
		bite: 4,
		d: "4 to cross, 18 to linger. Burns out fast",
	},
	sludge: {
		n: "Sludge",
		c: "#3f5c22",
		end: 6,
		enter: 3,
		life: 999,
		bite: 4,
		d: "Thick and foul. Costs 3 to wade into. Permanent",
	},
	spores: {
		n: "Spores",
		c: "#b9d99b",
		end: 6,
		enter: 1,
		life: 999,
		los: 1,
		bite: 3,
		d: "Blinds you and rots you slowly. Permanent",
	},
	miasma: {
		n: "Miasma",
		c: "#6fa88c",
		end: 10,
		enter: 1,
		life: 4,
		los: 1,
		sap: 2,
		d: "Blinds you, 10 damage, and drains 2 energy",
	},
	necrosis: {
		n: "Necrosis",
		c: "#2fd66b",
		end: 20,
		enter: 1,
		life: 3,
		bite: 6,
		d: "6 to cross, 20 to linger",
	},
	entropy: {
		n: "Entropy",
		c: "#39ff9e",
		end: 30,
		enter: 1,
		life: 999,
		bite: 10,
		d: "10 to cross, 30 to linger. Permanent",
	},
	grove: {
		n: "Life",
		c: "#b6f5c4",
		end: 0,
		enter: 1,
		life: 999,
		heal: 8,
		d: "Heals 8 if you end your turn here. Permanent",
	},
	phoenix: {
		n: "Phoenix",
		c: "#ffc48a",
		end: 0,
		enter: 1,
		life: 2,
		heal: 14,
		d: "Heals 14 a turn, but fades in two rounds",
	},
	spring: {
		n: "Spring",
		c: "#8fe8d8",
		end: 0,
		enter: 1,
		life: 999,
		heal: 6,
		d: "Heals 6 a turn. Permanent",
	},
	orchard: {
		n: "Orchard",
		c: "#84c47a",
		end: 0,
		enter: 2,
		life: 999,
		los: 1,
		heal: 5,
		d: "Blinds you but heals 5 a turn. Permanent",
	},
	zephyr: {
		n: "Zephyr",
		c: "#d8fff0",
		end: 0,
		enter: 0,
		life: 5,
		heal: 4,
		d: "Free to cross and heals 4 a turn",
	},
	pulse: {
		n: "Pulse",
		c: "#fff0a8",
		end: 0,
		enter: 1,
		life: 3,
		heal: 15,
		d: "Heals 15 a turn for three rounds",
	},
	rebirth: {
		n: "Rebirth",
		c: "#ffffff",
		end: 0,
		enter: 1,
		life: 2,
		heal: 25,
		d: "Heals 25 a turn, but only for two rounds",
	},
	rot: {
		n: "Rot",
		c: "#5c6b3a",
		end: 15,
		enter: 1,
		life: 999,
		bite: 5,
		d: "Life gone wrong. 5 to cross, 15 to linger. Permanent",
	},
	ironground: {
		n: "Iron",
		c: "#9aa7b8",
		end: 0,
		enter: 2,
		life: 999,
		anchor: 1,
		d: "Slow to step onto, but nothing can shove you off it. Permanent",
	},
	forge: {
		n: "Forge",
		c: "#ff8c42",
		end: 14,
		enter: 2,
		life: 999,
		anchor: 1,
		d: "Molten metal. 14 a turn, and you cannot be shoved out. Permanent",
	},
	quench: {
		n: "Quench",
		c: "#7fb2c9",
		end: 0,
		enter: 1,
		life: 999,
		anchor: 1,
		heal: 4,
		d: "Cool iron. Heals 4 a turn and holds you steady. Permanent",
	},
	bastion: {
		n: "Bastion",
		c: "#8b96a6",
		end: 0,
		enter: 2,
		life: 999,
		anchor: 1,
		los: 1,
		d: "Blinds you, but you cannot be moved. Permanent",
	},
	shrapnel: {
		n: "Shrapnel",
		c: "#c2c9d4",
		end: 6,
		enter: 1,
		life: 4,
		bite: 8,
		d: "8 to cross, 6 more to linger",
	},
	dynamo: {
		n: "Dynamo",
		c: "#ffe066",
		end: 0,
		enter: 1,
		life: 4,
		gain: 2,
		d: "End your turn here for 2 extra energy next turn",
	},
	lodestone: {
		n: "Lodestone",
		c: "#a88fd8",
		end: 0,
		enter: 2,
		life: 999,
		pull: 3,
		anchor: 1,
		d: "Drags in anyone ending a turn within three tiles. Permanent",
	},
	rust: {
		n: "Rust",
		c: "#a35a2a",
		end: 12,
		enter: 1,
		life: 999,
		bite: 6,
		d: "6 to cross, 12 to linger. Permanent",
	},
	bulwark: {
		n: "Bulwark",
		c: "#c8d6c0",
		end: 0,
		enter: 2,
		life: 999,
		anchor: 1,
		heal: 10,
		d: "Heals 10 a turn and roots you against shoves. Permanent",
	},
	resonance: {
		n: "Echo",
		c: "#d98fe0",
		end: 0,
		enter: 1,
		life: 999,
		aura: 5,
		rad: 2,
		d: "Hits everyone ending a turn within two tiles for 5. Permanent",
	},
	ashfall: {
		n: "Ashfall",
		c: "#ff9d6b",
		end: 6,
		enter: 1,
		life: 4,
		aura: 6,
		rad: 2,
		d: "6 to linger, and 6 to anyone within two tiles",
	},
	ripple: {
		n: "Ripple",
		c: "#6fd3ff",
		end: 0,
		enter: 1,
		life: 999,
		aura: 4,
		rad: 2,
		flow: 1,
		d: "Carries you onward, and hits everyone within two for 4. Permanent",
	},
	tremor: {
		n: "Tremor",
		c: "#b09068",
		end: 0,
		enter: 2,
		life: 999,
		aura: 7,
		rad: 2,
		d: "Hits everyone within two tiles for 7. Permanent",
	},
	howl: {
		n: "Howl",
		c: "#bff5e6",
		end: 8,
		enter: 1,
		life: 4,
		aura: 4,
		rad: 3,
		d: "8 to linger in, and 4 to anyone within three tiles",
	},
	thunder: {
		n: "Thunder",
		c: "#ffe38a",
		end: 5,
		enter: 1,
		life: 3,
		aura: 9,
		rad: 2,
		d: "5 to linger, and 9 to anyone within two tiles",
	},
	singularity: {
		n: "Singularity",
		c: "#e0b3ff",
		end: 0,
		enter: 1,
		life: 999,
		aura: 12,
		rad: 3,
		pull: 2,
		d: "Drags you in and hits everyone within three for 12. Permanent",
	},
	contagion: {
		n: "Contagion",
		c: "#a8d94f",
		end: 10,
		enter: 1,
		life: 999,
		aura: 8,
		rad: 2,
		bite: 5,
		d: "5 to cross, 10 to linger, 8 to anyone within two. Permanent",
	},
	harmony: {
		n: "Harmony",
		c: "#ffd9f2",
		end: 0,
		enter: 1,
		life: 999,
		auraHeal: 8,
		rad: 2,
		d: "Heals everyone within two tiles for 8 a turn. Permanent",
	},
	firestorm: {n: "Firestorm", c: "#ff3b00", end: 22, enter: 1, life: 4, d: "22 a turn. Fire fed on fire"},
	deluge: {
		n: "Deluge",
		c: "#0d7fe0",
		end: 0,
		enter: 0,
		life: 999,
		flow: 1,
		d: "A current. Free to enter and it carries you on. Permanent",
	},
	mountain: {
		n: "Mountain",
		c: "#6b5533",
		end: 0,
		enter: 99,
		life: 999,
		solid: 1,
		d: "An impassable peak. Permanent",
	},
	tempest: {
		n: "Tempest",
		c: "#7fffe4",
		end: 0,
		enter: 0,
		life: 5,
		gain: 3,
		d: "Free to cross and worth 3 energy next turn",
	},
	overload: {
		n: "Overload",
		c: "#fff07a",
		end: 14,
		enter: 1,
		life: 3,
		sap: 3,
		d: "14 a turn and drains 3 energy",
	},
	abyss: {
		n: "Abyss",
		c: "#8c5aff",
		end: 0,
		enter: 1,
		life: 999,
		gone: 1,
		spread: 1,
		d: "A 3x3 hole in the world. Anyone who touches it is gone",
	},
	plague: {
		n: "Plague",
		c: "#5aff2f",
		end: 25,
		enter: 1,
		life: 999,
		bite: 10,
		d: "10 to cross, 25 to linger. Permanent",
	},
	eden: {n: "Eden", c: "#ffffff", end: 0, enter: 1, life: 999, heal: 20, d: "Heals 20 a turn. Permanent"},
	adamant: {
		n: "Adamant",
		c: "#dfe6f0",
		end: 0,
		enter: 2,
		life: 999,
		anchor: 1,
		ward: 1,
		d: "No ground effect can touch you here, and nothing can shove you. Permanent",
	},
	cacophony: {
		n: "Cacophony",
		c: "#ff6fe0",
		end: 0,
		enter: 1,
		life: 999,
		aura: 15,
		rad: 3,
		d: "Hits everyone ending a turn within three tiles for 15. Permanent",
	},
	shadow: {
		n: "Shadow",
		c: "#6b5f8f",
		end: 0,
		enter: 1,
		life: 999,
		hide: 1,
		d: "Nobody can see you while you stand here. Permanent",
	},
	emberveil: {
		n: "Emberveil",
		c: "#b5563f",
		end: 8,
		enter: 1,
		life: 4,
		hide: 1,
		d: "Hides you, and burns for 8 a turn",
	},
	gloom: {
		n: "Gloom",
		c: "#4a6a8c",
		end: 0,
		enter: 1,
		life: 999,
		hide: 1,
		flow: 1,
		d: "Hides you and carries you onward. Permanent",
	},
	cavern: {
		n: "Cavern",
		c: "#5b5346",
		end: 0,
		enter: 2,
		life: 999,
		hide: 1,
		los: 1,
		d: "Hides you from them and them from you. Permanent",
	},
	shade: {
		n: "Shade",
		c: "#9aa8bd",
		end: 0,
		enter: 0,
		life: 5,
		hide: 1,
		d: "Free to cross, and hides you",
	},
	flicker: {
		n: "Flicker",
		c: "#c9a86b",
		end: 7,
		enter: 1,
		life: 3,
		hide: 1,
		d: "Hides you, and shocks for 7 a turn",
	},
	eclipse: {
		n: "Eclipse",
		c: "#3b2f5c",
		end: 16,
		enter: 1,
		life: 999,
		hide: 1,
		d: "Hides you, and takes 16 a turn. Permanent",
	},
	murk: {
		n: "Murk",
		c: "#4f6b3f",
		end: 14,
		enter: 1,
		life: 999,
		hide: 1,
		bite: 5,
		d: "Hides you. 5 to cross, 14 to linger. Permanent",
	},
	haven: {
		n: "Haven",
		c: "#b8b0d0",
		end: 0,
		enter: 1,
		life: 999,
		hide: 1,
		heal: 9,
		d: "Hides you and heals 9 a turn. Permanent",
	},
	bunker: {
		n: "Bunker",
		c: "#7b7a8c",
		end: 0,
		enter: 2,
		life: 999,
		hide: 1,
		anchor: 1,
		ward: 1,
		d: "Hides you, holds you steady, and no ground effect can touch you. Permanent",
	},
	whisper: {
		n: "Whisper",
		c: "#a78fd0",
		end: 0,
		enter: 1,
		life: 999,
		hide: 1,
		aura: 8,
		rad: 2,
		d: "Hides you, and hits everyone within two for 8. Permanent",
	},
	oblivion: {
		n: "Oblivion",
		c: "#241d3d",
		end: 20,
		enter: 1,
		life: 999,
		hide: 1,
		anchor: 1,
		d: "Hides you, holds you, and takes 20 a turn. Permanent",
	},
	frost: {
		n: "Frost",
		c: "#8fd8f0",
		end: 4,
		enter: 1,
		life: 999,
		root: 1,
		d: "Step in and you are frozen until the end of your next turn. Permanent",
	},
	meltwater: {
		n: "Meltwater",
		c: "#5fb8d8",
		end: 0,
		enter: 1,
		life: 5,
		flow: 1,
		d: "Frost gone soft. Carries you onward",
	},
	glacier: {
		n: "Glacier",
		c: "#bfe8f5",
		end: 0,
		enter: 99,
		life: 6,
		solid: 1,
		melts: "meltwater",
		d: "A wall of ice. Nothing passes for six rounds, then it melts into Meltwater",
	},
	permafrost: {
		n: "Permafrost",
		c: "#9db8c4",
		end: 6,
		enter: 2,
		life: 999,
		root: 1,
		anchor: 1,
		d: "Frozen solid. 6 a turn, and you can move neither yourself nor be shoved. Permanent",
	},
	blizzard: {
		n: "Blizzard",
		c: "#dff2ff",
		end: 8,
		enter: 1,
		life: 4,
		root: 1,
		aura: 5,
		rad: 2,
		d: "Freezes you, 8 a turn, and 5 to anyone within two",
	},
	hail: {
		n: "Hail",
		c: "#c8e4ff",
		end: 12,
		enter: 1,
		life: 3,
		root: 1,
		d: "Freezes you and hammers for 12 a turn",
	},
	absolute: {
		n: "Absolute Zero",
		c: "#e8f6ff",
		end: 22,
		enter: 1,
		life: 999,
		root: 1,
		d: "Freezes you and takes 22 a turn. Permanent",
	},
	rime: {
		n: "Rime",
		c: "#a8d8b0",
		end: 13,
		enter: 1,
		life: 999,
		root: 1,
		bite: 5,
		d: "Freezes you. 5 to cross, 13 to linger. Permanent",
	},
	hibernation: {
		n: "Hibernation",
		c: "#dcefe8",
		end: 0,
		enter: 1,
		life: 999,
		root: 1,
		heal: 14,
		d: "Freezes you, but heals 14 a turn. Permanent",
	},
	coldiron: {
		n: "Coldiron",
		c: "#9fb4c0",
		end: 0,
		enter: 2,
		life: 999,
		root: 1,
		anchor: 1,
		ward: 1,
		d: "Freezes and holds you, but no ground effect can touch you. Permanent",
	},
	chill: {
		n: "Chill",
		c: "#c0a8e0",
		end: 0,
		enter: 1,
		life: 999,
		root: 1,
		aura: 9,
		rad: 2,
		d: "Freezes you, and hits everyone within two for 9. Permanent",
	},
	whiteout: {
		n: "Whiteout",
		c: "#b8bcd8",
		end: 5,
		enter: 1,
		life: 999,
		root: 1,
		hide: 1,
		d: "Freezes and hides you, 5 a turn. Permanent",
	},
	deepfreeze: {
		n: "Deepfreeze",
		c: "#7fc8e8",
		end: 18,
		enter: 1,
		life: 999,
		root: 1,
		anchor: 1,
		d: "Frozen to the bone. 18 a turn, and nothing moves you. Permanent",
	},
	light: {
		n: "Light",
		c: "#fff3b0",
		end: 0,
		enter: 1,
		life: 999,
		reveal: 2,
		d: "Nobody within two squares can hide. Permanent",
	},
	flare: {
		n: "Flare",
		c: "#ffb347",
		end: 9,
		enter: 1,
		life: 4,
		reveal: 2,
		d: "9 a turn, and nobody within two can hide",
	},
	shimmer: {
		n: "Shimmer",
		c: "#bfe9ff",
		end: 0,
		enter: 1,
		life: 999,
		reveal: 2,
		flow: 1,
		d: "Carries you on, and nobody within two can hide. Permanent",
	},
	beacon: {
		n: "Beacon",
		c: "#d8c68a",
		end: 0,
		enter: 2,
		life: 999,
		reveal: 3,
		anchor: 1,
		d: "Holds you fast, and nobody within three can hide. Permanent",
	},
	dawn: {
		n: "Dawn",
		c: "#ffe9c9",
		end: 0,
		enter: 0,
		life: 5,
		reveal: 2,
		d: "Free to cross, and nobody within two can hide",
	},
	flash: {
		n: "Flash",
		c: "#fff59a",
		end: 11,
		enter: 1,
		life: 3,
		reveal: 3,
		d: "11 a turn, and nobody within three can hide",
	},
	starfall: {
		n: "Starfall",
		c: "#e8d0ff",
		end: 19,
		enter: 1,
		life: 999,
		reveal: 2,
		d: "19 a turn, and nobody within two can hide. Permanent",
	},
	blister: {
		n: "Blister",
		c: "#d9e06a",
		end: 13,
		enter: 1,
		life: 999,
		reveal: 2,
		bite: 5,
		d: "5 to cross, 13 to linger, and no hiding within two. Permanent",
	},
	sanctuary: {
		n: "Sanctuary",
		c: "#fff6e0",
		end: 0,
		enter: 1,
		life: 999,
		reveal: 2,
		heal: 11,
		d: "Heals 11 a turn, and nobody within two can hide. Permanent",
	},
	lantern: {
		n: "Lantern",
		c: "#e0d3a8",
		end: 0,
		enter: 2,
		life: 999,
		reveal: 3,
		anchor: 1,
		ward: 1,
		d: "No ground effect touches you, nothing moves you, nobody within three hides. Permanent",
	},
	radiance: {
		n: "Radiance",
		c: "#ffd6f0",
		end: 0,
		enter: 1,
		life: 999,
		reveal: 2,
		aura: 10,
		rad: 2,
		d: "10 to everyone within two, and none of them can hide. Permanent",
	},
	twilight: {
		n: "Twilight",
		c: "#8c86a8",
		end: 7,
		enter: 1,
		life: 999,
		d: "Light and shadow cancel. Just 7 a turn. Permanent",
	},
	glare: {
		n: "Glare",
		c: "#cfe8f5",
		end: 6,
		enter: 1,
		life: 999,
		reveal: 2,
		root: 1,
		d: "Freezes you, 6 a turn, and no hiding within two. Permanent",
	},
	dayburst: {
		n: "Dayburst",
		c: "#fffbe0",
		end: 15,
		enter: 1,
		life: 999,
		reveal: 4,
		d: "15 a turn, and nobody within four squares can hide. Permanent",
	},
	collapsed: {
		n: "Collapsed",
		c: "#ff2f4a",
		end: 0,
		enter: 99,
		life: 999,
		solid: 1,
		dead: 1,
		gone: 1,
		d: "This square fell away. Anything in it is gone, and nothing can enter again",
	},
	bell: {
		n: "Bell",
		c: "#c4c9d8",
		end: 0,
		enter: 2,
		life: 999,
		anchor: 1,
		aura: 5,
		rad: 3,
		d: "Holds you steady and hits everyone within three for 5. Permanent",
	},
};
